import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    Grid,
    IconButton,
    Skeleton,
    Typography,
    useTheme,
} from "@mui/material";
import { observer } from "mobx-react";
import React, { MouseEvent, useRef, useState } from "react";
import { AcxAgentService } from "services/AcxAgentService";
import moment from "moment";
import { sxClsx } from "utils/styleClsx";
import AgentStore from "../Stores/AgentStore";
import { useStore } from "utils/useStore";
import {
    DeleteOutlineRounded,
    EditRounded,
    Error as ErrorIcon,
} from "@mui/icons-material";
import MessageStore from "components/ManagerInteractions/Stores/MessageStore";
import AcxButton from "components/UI/AcxButton";
import SingleAgentChatStore from "../Stores/SingleAgentChatStore";

import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { AgentState } from "../Models/AgentState";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import AcxMenu, { AcxMenuItemProps } from "components/UI/Menu/AcxMenu";

export const SidebarChat = observer(
    ({
        selected,
        chatStore,
        abortControllerRef,
    }: {
        selected: boolean;
        chatStore: SingleAgentChatStore;
        abortControllerRef: React.MutableRefObject<AbortController | null>;
    }) => {
        const fallbackChatName = `Chat From ${moment(
            chatStore.chat.createdOn,
        ).format("MMM D, hh:mm A")}`;
        const theme = useTheme();
        const [hover, setHover] = useState(false);
        const agentStore = useStore(AgentStore);
        const agentService = new AcxAgentService();
        const messageStore = useStore(MessageStore);
        const isUnreadKboFlowChat = agentStore.isKboFlowChatUnread(
            chatStore.chat,
        );
        const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
        const [renaming, setRenaming] = useState(false);
        const [renameValue, setRenameValue] = useState(
            chatStore.chat.name || fallbackChatName,
        );

        // Handle chat selection
        const onChatSelect = async () => {
            if (agentStore.currentChatId === chatStore.chat.id) {
                return; // Already selected
            }
            if (chatStore.chatLoaded) {
                // This chat has already been loaded, use the store data instead of the fetched data
                agentStore.setCurrentChatId(chatStore.chat.id);
                agentStore.setIsEmpty(false);
                agentStore.setLoadingCurrentChat(false);
                window.history.pushState(
                    {},
                    "",
                    `/app/kbo/chats/${chatStore.chat.id}`,
                );
                agentStore.markKboFlowChatRead(chatStore.chat);
                return;
            }
            agentStore.setLoadingCurrentChat(true);
            try {
                // abort any ongoing fetches for this chat
                abortControllerRef.current?.abort();
                abortControllerRef.current = new AbortController();
                const chatSession = await agentService.getChatById({
                    chatId: chatStore.chat.id,
                    abortSignal: abortControllerRef.current?.signal,
                });
                if (!chatSession) {
                    throw new Error("Chat session not found");
                }
                // Update our local chat store with the fetched chat session
                chatStore.loadFromChatSession(chatSession);
                chatStore.setChatLoaded(true);
                agentStore.setCurrentChatId(chatStore.chat.id);
                agentStore.setIsEmpty(false);
                window.history.pushState(
                    {},
                    "",
                    `/app/kbo/chats/${chatStore.chat.id}`,
                );
                agentStore.markKboFlowChatRead(chatStore.chat);
                agentStore.setLoadingCurrentChat(false);
            } catch (e) {
                console.error("Error selecting chat:", e);
                messageStore.logError(
                    "Failed to load chat. Please try again later.",
                );
            }
        };

        // Handle chat name change
        const handleChatNameChange = async (newName: string) => {
            if (newName === chatStore.chat.name || newName.trim() === "") {
                setRenaming(false);
                setRenameValue(chatStore.chat.name || fallbackChatName);
                return;
            }
            const oldName = chatStore.chat.name;
            try {
                chatStore.updateName(newName.trim());
                setRenameValue(newName.trim());
                setRenaming(false);
                await agentService.setChatName(
                    chatStore.chat.id,
                    newName.trim(),
                );
            } catch (error) {
                console.error("Error updating chat name:", error);
                chatStore.updateName(oldName);
                setRenameValue(oldName || fallbackChatName);
                messageStore.logError(
                    "Failed to update chat name. Please try again later.",
                );
            }
        };

        const onDelete = async () => {
            if (agentStore.currentChatId === chatStore.chat.id) {
                // Kick you back to empty if you delete the chat you're on
                agentStore.setIsEmpty(true);
                agentStore.setCurrentChatId(emptyGUID);
                window.history.replaceState({}, "", `/app/kbo/chats`);
            }
            agentStore.removeChat(chatStore.chat);
            const response = await agentService.deleteChatById(
                chatStore.chat.id,
            );
            if (response.ok) {
                messageStore.logMessage(
                    "Chat deleted successfully.",
                    "success",
                );
            } else {
                agentStore.addChat(chatStore.chat);
                messageStore.logError(
                    "Failed to delete chat. Please try again later.",
                );
            }
        };

        const preloadFullChat = async (chatId: string) => {
            if (!chatId || chatStore.chatLoaded) {
                // If chatId is empty or the chat store is already loaded, skip preloading
                return;
            }
            if (selected) {
                // If currently selected, we don't need to preload this one
                return;
            }
            try {
                // Fetch full chat data in the background
                const fullChatData = await agentService.getChatById({
                    chatId,
                    abortSignal: abortControllerRef.current?.signal,
                });
                if (!fullChatData) {
                    throw new Error("Failed to prefetch full chat data");
                }
                chatStore.loadFromChatSession(fullChatData);
                chatStore.setChatLoaded(true);
            } catch (error: any) {
                if (
                    error instanceof DOMException &&
                    error.name === "AbortError"
                ) {
                    // Fetch was aborted, ignore this error
                    return;
                }
                console.error("Error preloading full chat:", error);
            }
        };

        // More menu things
        const [moreMenuAnchorEl, setMoreMenuAnchorEl] =
            useState<null | HTMLElement>(null);

        function handleMoreMenuClick(event: MouseEvent<HTMLButtonElement>) {
            event.preventDefault();
            event.stopPropagation();
            setMoreMenuAnchorEl(moreMenuAnchor.current);
        }

        function handleMoreMenuClose() {
            setMoreMenuAnchorEl(null);
        }

        const moreMenuAnchor = useRef<HTMLDivElement | null>(null);
        const menuItems: ("Divider" | AcxMenuItemProps)[] = [
            {
                id: `kbo-chat-rename`,
                props: {
                    onClick: () => {
                        setRenaming(true);
                        handleMoreMenuClose();
                    },
                },
                label: <Typography>Rename</Typography>,
                icon: <EditRounded />,
            },
            "Divider",
            {
                id: `kbo-chat-delete`,
                props: {
                    onClick: () => {
                        setDeleteDialogOpen(true);
                        handleMoreMenuClose();
                    },
                },
                label: <Typography>Delete</Typography>,
                icon: <DeleteOutlineRounded />,
            },
        ];

        return (
            <>
                <Grid
                    id="kbo-fullscreen-history-chat"
                    sx={sxClsx(
                        {
                            display: "flex",
                            flexDirection: "row",
                            paddingY: "6px",
                            px: 1,
                            transition:
                                "background-color 0.1s, border-right 0.1s",
                            cursor: "pointer",
                            borderRadius: 2,
                            justifyContent: "space-between",
                            marginRight: 2,
                            alignItems: "center",
                        },
                        hover && {
                            backgroundColor: theme.palette.gray[200],
                            backgroundOpacity: 0.5,
                        },
                        selected && {
                            backgroundColor: theme.palette.gray[200],
                        },
                    )}
                    onMouseEnter={() => {
                        setHover(true);
                        preloadFullChat(chatStore.chat.id);
                    }}
                    onMouseLeave={() => setHover(false)}
                    onClick={() => {
                        onChatSelect();
                    }}
                    ref={moreMenuAnchor}
                >
                    <Grid
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0,
                            flex: 1,
                            minWidth: 0,
                        }}
                    >
                        {renaming ? (
                            <input
                                id="kbo-edit-chat-name"
                                data-testid="chat-name-input"
                                type="text"
                                style={{
                                    background: theme.palette.white.main,
                                    border: `1px solid ${theme.palette.primary.main}`,
                                    borderRadius: theme.shape.borderRadius,
                                    padding: `${theme.spacing(
                                        0.5,
                                    )} ${theme.spacing(1)}`,
                                    height: "20px",
                                    width: "100%",
                                }}
                                value={renameValue}
                                onBlur={(e) => {
                                    handleChatNameChange(e.target.value);
                                }}
                                onChange={(e) => setRenameValue(e.target.value)}
                                onKeyDown={(e) => {
                                    // Pressing enter should submit
                                    if (e.key === "Enter") {
                                        handleChatNameChange(renameValue);
                                    }
                                    // Pressing escape should cancel
                                    if (e.key === "Escape") {
                                        setRenaming(false);
                                        setRenameValue(
                                            chatStore.chat.name ||
                                                fallbackChatName,
                                        );
                                    }
                                }}
                                autoFocus
                            />
                        ) : (
                            <Grid
                                sx={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: 0.75,
                                    minWidth: 0,
                                }}
                            >
                                <Typography
                                    sx={{
                                        color: theme.palette.neutral[600],
                                        fontSize: "14px",
                                        fontWeight: isUnreadKboFlowChat
                                            ? 700
                                            : 500,
                                        lineHeight: "20px",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        wordBreak: "break-word",
                                        maxHeight: "40px", // 2 lines * 20px lineHeight
                                        minWidth: 0,
                                    }}
                                >
                                    {chatStore.chat.name ===
                                        "Generating..." && (
                                        <Skeleton
                                            width="100%"
                                            height={9}
                                            sx={{ borderRadius: "26px" }}
                                        />
                                    )}
                                    {chatStore.chat.name !== "Generating..." &&
                                        (chatStore.chat.name
                                            ? chatStore.chat.name
                                            : fallbackChatName)}
                                </Typography>
                                {isUnreadKboFlowChat && (
                                    <Box
                                        aria-label="Unread KBo Flow finding"
                                        data-testid="kbo-flow-unread-indicator"
                                        role="img"
                                        sx={{
                                            backgroundColor:
                                                theme.palette.secondary.main,
                                            borderRadius: "50%",
                                            flex: "0 0 auto",
                                            height: 7,
                                            marginTop: "6px",
                                            width: 7,
                                        }}
                                    />
                                )}
                            </Grid>
                        )}
                        <Typography
                            sx={{
                                color: theme.palette.gray[400],
                                fontSize: "12px",
                                fontWeight: 500,
                                lineHeight: "20px",
                            }}
                        >
                            {chatStore.state === AgentState.Idle &&
                                moment(chatStore.chat.createdOn).format(
                                    "MMM D",
                                )}
                            {(chatStore.state === AgentState.Waiting ||
                                chatStore.state === AgentState.Streaming) && (
                                <i>Working on your request...</i>
                            )}
                        </Typography>
                    </Grid>

                    <IconButton
                        id={`kbo-chat-menu`}
                        sx={sxClsx(
                            {
                                color: theme.palette.black.main,
                                display: "none",
                                height: "30px",
                                width: "30px",
                            },
                            (hover || moreMenuAnchorEl) && {
                                backgroundColor: theme.palette.gray[200],
                                display: "flex",
                            },
                        )}
                        onClick={handleMoreMenuClick}
                        size="small"
                    >
                        <MoreVertIcon sx={{ padding: "1px" }} />
                    </IconButton>
                </Grid>
                <ConfirmDeleteDialog
                    isOpen={deleteDialogOpen}
                    setIsOpen={() => setDeleteDialogOpen(false)}
                    onDelete={async () => {
                        setDeleteDialogOpen(false);
                        await onDelete();
                    }}
                />
                <AcxMenu
                    menuItems={menuItems}
                    anchorElement={moreMenuAnchorEl}
                    onMenuClose={handleMoreMenuClose}
                    anchorOrigin={{
                        vertical: "top",
                        horizontal: "right",
                    }}
                    transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                    }}
                />
            </>
        );
    },
);

const ConfirmDeleteDialog = ({ isOpen, setIsOpen, onDelete }) => {
    const theme = useTheme();
    return (
        <Dialog open={isOpen} onClose={() => setIsOpen(false)}>
            <DialogContent
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
            >
                <ErrorIcon
                    sx={{
                        color: theme.palette.error[500],
                        width: "40px",
                        height: "40px",
                    }}
                />
                <Typography
                    sx={{
                        fontWeight: 600,
                        fontSize: "20px",
                        color: theme.palette.gray[700],
                        lineHeight: "32px",
                    }}
                >
                    Delete chat?
                </Typography>
                <DialogContentText
                    sx={{
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        color: theme.palette.gray[500],
                    }}
                >
                    Deleting this chat will delete your prompts and KBo's
                    responses. This action cannot be undone.
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <AcxButton
                    onClick={() => setIsOpen(false)}
                    variant="contained"
                    color="white"
                >
                    Cancel
                </AcxButton>
                <AcxButton
                    onClick={() => {
                        onDelete();
                    }}
                    variant="contained"
                    color="error"
                >
                    Delete
                </AcxButton>
            </DialogActions>
        </Dialog>
    );
};
